// src/components/admin/CommunicationsAIAgent.jsx
// "Communications AI" page under Communications Management: the admin types an instruction ("Create an e-mail for New
// Year and send it to all customers") and the agent asks for what's missing, writes or designs the content, and sends
// it. Every send to customers comes back as an approval card with a preview, and only goes out when the admin presses
// Approve.
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { CommunicationsAgentService } from '../../api/backendAiService';
import DailyTokenCounter from './DailyTokenCounter';
import '../../styles/bookings-ai-agent.css';

const EXAMPLES = [
  'Create an email for New Year and send it to all customers.',
  'Send a push notification about our running promotion to Gold and Platinum members.',
  'WhatsApp customers who haven\'t visited in 3 months with a "we miss you" message.',
  'Design a Christmas newsletter e-mail and send me a test first.',
  'How did my last e-mail campaign do? Which one had the best open rate?',
  'What campaigns are scheduled, and cancel the one going out tomorrow.',
  'Limit push notifications to 3 per week per customer.',
];

const APPROVAL_RULES = [
  'Every e-mail, push notification or WhatsApp message to customers - you see the content, audience and counts first',
  'WhatsApp sends (each message is charged to the salon)',
  'Changing a channel\'s frequency limit',
  'More than 5 changes from one instruction',
];

const CHANNEL_LABEL = { email: 'E-mail', push: 'Push notification', whatsapp: 'WhatsApp' };

// Designed e-mails are shown in a sandboxed frame so their HTML/CSS can't touch the admin page
function EmailFrame({ html }) {
  return <iframe className="cai-email-frame" title="E-mail preview" sandbox="" srcDoc={html} />;
}

function ContentPreview({ preview }) {
  const [open, setOpen] = useState(preview.channel !== 'email' || !preview.html);
  return (
    <div className={`cai-preview cai-preview--${preview.channel}`}>
      <div className="cai-preview-head">
        <span>{CHANNEL_LABEL[preview.channel]} preview</span>
        {preview.html && <button className="bai-link" onClick={() => setOpen(o => !o)}>{open ? 'Hide design' : 'Show design'}</button>}
      </div>
      {preview.subject && (
        <div className="cai-preview-subject">
          {preview.channel === 'whatsapp' ? <span>Campaign: {preview.subject}</span> : <strong>{preview.subject}</strong>}
        </div>
      )}
      {preview.imageUrl && <img className="cai-preview-image" src={preview.imageUrl} alt="" />}
      {preview.body && <div className="cai-preview-body">{preview.body}</div>}
      {preview.html && open && <EmailFrame html={preview.html} />}
      {(preview.body || preview.html) && /\{[A-Za-z_]+\}/.test(`${preview.subject} ${preview.body || ''} ${preview.html || ''}`) && (
        <div className="cai-preview-note">Words in {'{curly braces}'} are replaced with each customer's details.</div>
      )}
    </div>
  );
}

function CommunicationsAIAgent({ salonId, onOpenCampaignHistory }) {
  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Tell me what you'd like to send - for example a New Year e-mail to all customers, a push notification about a promotion, or a WhatsApp message to customers who haven't visited in a while. I'll ask for anything missing, write or design the message, and show you exactly who it reaches. Nothing goes to customers until you press Approve.",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  // actionId -> { state: 'pending' | 'working' | 'done' | 'declined' | 'failed', message }
  const [approvals, setApprovals] = useState({});
  const serviceRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Approve/decline outcomes the agent hasn't been told about yet; sent along with the next instruction
  const unreportedOutcomes = useRef([]);

  useEffect(() => {
    if (salonId) serviceRef.current = new CommunicationsAgentService(salonId);
  }, [salonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  const addMessage = (msg) => setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    const outcomes = unreportedOutcomes.current;
    unreportedOutcomes.current = [];
    const outgoing = outcomes.length
      ? `[Update since your last reply: ${outcomes.join(' ')}]\n\n${text}`
      : text;

    try {
      const res = await serviceRef.current.sendMessage(outgoing);
      setApprovals(prev => {
        const next = { ...prev };
        res.pendingActions.forEach(p => { next[p.actionId] = { state: 'pending' }; });
        return next;
      });
      addMessage({ type: 'ai', content: res.message, actions: res.actions, pendingActions: res.pendingActions, drafts: res.drafts });
      if (res.dailyUsage) tokenCounterRef.current?.update(res.dailyUsage);
    } catch (error) {
      console.error('Communications AI agent error:', error);
      unreportedOutcomes.current = [...outcomes, ...unreportedOutcomes.current];
      const content = error?.status === 429
        ? 'You have reached the daily AI usage limit. Communications AI is available again after midnight.'
        : error?.status === 403
          ? (error.message || 'Communications AI is not available for your account.')
          : "Something went wrong and I couldn't finish. Please check Campaign History before trying again - a test send may already have gone out.";
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  const handleDecision = async (action, decision) => {
    setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'working' } }));
    try {
      const res = await serviceRef.current.resolveAction(action.actionId, decision);
      if (res.declined) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'declined', message: 'Declined - nothing was sent.' } }));
        unreportedOutcomes.current.push(`The admin DECLINED "${action.title}", so it was not done.`);
      } else if (res.success) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'done', message: res.message } }));
        unreportedOutcomes.current.push(`The admin APPROVED "${action.title}" and it was completed: ${res.message}`);
      } else {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: res.message } }));
        unreportedOutcomes.current.push(`The admin approved "${action.title}" but it failed: ${res.message}`);
      }
    } catch (error) {
      setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: error.message } }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const historyLink = onOpenCampaignHistory && <button className="bai-link" onClick={onOpenCampaignHistory}>Open Campaign History →</button>;

  const renderApprovalCard = (p) => {
    const status = approvals[p.actionId] || { state: 'pending' };
    return (
      <div key={p.actionId} className={`bai-approval-card bai-approval-card--${status.state}`}>
        <div className="bai-approval-head">
          <span className="bai-approval-badge">Needs your approval</span>
          <strong>{p.title}</strong>
        </div>
        {p.lines?.map((line, i) => <div key={i} className="bai-approval-line">{line}</div>)}
        {p.preview && <ContentPreview preview={p.preview} />}
        <div className="bai-approval-reason">{p.reason}</div>
        {(status.state === 'pending' || status.state === 'working') && (
          <div className="bai-approval-actions">
            <button className="bai-btn bai-btn--approve" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'approve')}>
              {status.state === 'working' ? 'Working…' : p.type === 'send_campaign' ? 'Approve & send' : 'Approve'}
            </button>
            <button className="bai-btn bai-btn--decline" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'decline')}>
              Decline
            </button>
          </div>
        )}
        {status.state === 'done' && (
          <>
            <div className="bai-approval-result bai-approval-result--ok">✓ {status.message}</div>
            {p.type === 'send_campaign' && historyLink}
          </>
        )}
        {status.state === 'declined' && <div className="bai-approval-result">{status.message}</div>}
        {status.state === 'failed' && <div className="bai-approval-result bai-approval-result--err">Not done: {status.message}</div>}
      </div>
    );
  };

  return (
    <div className="ai-tab-container bookings-ai-agent communications-ai-agent">
      <div className="ai-tab-header">
        <h2>Communications AI</h2>
        <p>Type an instruction and the assistant will carry it out in Communications Management - writing or designing e-mails, push notifications and WhatsApp messages, choosing the audience, and sending or scheduling them, just like doing it by hand.</p>
      </div>

      <DailyTokenCounter ref={tokenCounterRef} salonId={salonId} onLimitReached={setLimitReached} />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try an instruction</h3>
          <div className="example-prompts">
            {EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <div className="bai-rules">
            <h4>Always asks for your approval</h4>
            <ul>{APPROVAL_RULES.map(r => <li key={r}>{r}</li>)}</ul>
            <p>Test sends go only to the salon's own admin accounts and run straight away. Recurring campaigns and portal personalisation are set up on their own pages.</p>
          </div>
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map(m => (
              <div key={m.id} className={`message ${m.type}-message`}>
                {m.type === 'ai' && <div className="ai-avatar">🤖</div>}
                {m.type === 'user' && <div className="user-avatar">👤</div>}
                {m.type === 'system' && <div className="system-avatar">ℹ️</div>}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  {m.drafts?.map(d => (
                    <ContentPreview key={d.draftId} preview={{ channel: 'email', subject: d.subject, html: d.html }} />
                  ))}
                  {m.actions?.length > 0 && (
                    <div className="bai-done-list">
                      {m.actions.map((a, i) => (
                        <div key={i} className="bai-done-item">
                          <span className="bai-done-check">✓</span>
                          <span>{a.message}</span>
                        </div>
                      ))}
                      {m.actions.some(a => a.type === 'cancel_scheduled_campaign') && historyLink}
                    </div>
                  )}
                  {m.pendingActions?.map(renderApprovalCard)}
                  <div className="message-time">{formatTime(m.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at midnight.' : 'e.g. Create an email for New Year and send it to all customers (Enter to send, Shift+Enter for a new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <span className="bai-hint">Designing an e-mail can take up to a minute.</span>
              <button className="send-btn" onClick={handleSend} disabled={isLoading || !input.trim() || limitReached}>
                {isLoading ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommunicationsAIAgent;
